import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import CertificadoGratuito from "../componentes/CertificadoGratuito";
import { dataDoDia } from "../componentes/FaculdadeUi";
import { CAMPO, Recado } from "../componentes/ui";
import {
  assinarPresenca,
  emitirCertificado,
  enderecoDaValidacao,
  lembrarQuemAssinou,
  lerCertificado,
  lerCurso,
  type AulaPublica,
  type CertificadoPublico,
  type CursoPublico,
  type Recusa,
} from "../lib/certificadosGratuitos";
import { mascararTelefone } from "../lib/mascaras";
import { Moldura } from "./Diagnostico";

// As páginas PÚBLICAS dos cursos gratuitos ao vivo (000380), que eram o certificados-cf
// (certificados.contabilidadefacilitada.com). Quem as usa não tem conta no hub: assina a presença
// na aula, emite o certificado no fim e qualquer um valida pelo QR. Moram em /certificados/, e na
// virada um redirecionamento leva o endereço de lá para cá, com o mesmo caminho depois do domínio.
//
//   /certificados/p/<curso>/<aula>     assinar a presença
//   /certificados/emitir/<curso>       emitir (o banco confere a presença: a decisão do Enzo)
//   /certificados/<token>              o certificado, para imprimir ou salvar em PDF
//   /certificados/verificar/<token>    a validação, que é para onde o QR leva
//
// O que mudou em relação a lá, e por quê: emitir exige a presença com o mesmo e-mail (lá qualquer
// um emitia), e pedir de novo devolve o mesmo certificado (lá cada visita criava outro). A tela
// não decide nada disso: ela mostra a frase do banco.

/** dd/mm de uma data AAAA-MM-DD, lida sem passar por `Date` (a cicatriz da data da colação). */
function diaEMes(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}` : iso;
}

function Aviso({ titulo, texto, tom = "atencao" }: { titulo: string; texto: ReactNode; tom?: "atencao" | "erro" }) {
  return (
    <Moldura>
      <div className="surface p-7 text-center" data-aviso={tom}>
        <h1 className="font-display text-xl font-bold text-titulo">{titulo}</h1>
        <div className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cinza">{texto}</div>
        <p className="mt-4 text-xs text-cinza">Se precisar, fale com a equipe de atendimento da Contabilidade Facilitada.</p>
      </div>
    </Moldura>
  );
}

/** O formulário das duas páginas: nome e e-mail, e o telefone só na presença. */
function FormularioDeDados({
  titulo,
  descricao,
  botao,
  comTelefone,
  inicial,
  aoEnviar,
}: {
  titulo: string;
  descricao: ReactNode;
  botao: string;
  comTelefone: boolean;
  inicial?: { nome: string; email: string } | null;
  aoEnviar: (d: { nome: string; email: string; telefone: string }) => Promise<Recusa | null>;
}) {
  const id = useId();
  const [nome, setNome] = useState(inicial?.nome ?? "");
  const [email, setEmail] = useState(inicial?.email ?? "");
  const [telefone, setTelefone] = useState("");
  const [enviando, setEnviando] = useState(false);
  // A recusa é do BANCO, com o campo que ela aponta e a frase dela.
  const [recusa, setRecusa] = useState<Recusa | null>(null);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setRecusa(null);
    const r = await aoEnviar({ nome, email, telefone });
    setEnviando(false);
    setRecusa(r);
  }

  const erroDe = (campo: string) =>
    recusa?.motivo === campo ? (
      <p id={`${id}-${campo}-erro`} className="mt-1 text-sm text-erro-tinta">
        {recusa.mensagem}
      </p>
    ) : null;
  const campos = ["nome", "email", "telefone"];

  return (
    <Moldura>
      <form className="surface space-y-4 p-6 sm:p-8" onSubmit={enviar} noValidate data-formulario>
        <div>
          <h1 className="font-display text-2xl font-bold text-titulo sm:text-3xl">{titulo}</h1>
          <p className="mt-2 text-sm leading-relaxed text-cinza">{descricao}</p>
        </div>
        <div>
          <label htmlFor={`${id}-nome`} className="text-sm font-semibold text-tinta">
            Nome completo
          </label>
          <input
            id={`${id}-nome`}
            className={`${CAMPO} mt-1`}
            autoComplete="name"
            placeholder="Como deve aparecer no certificado"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            aria-invalid={recusa?.motivo === "nome"}
            aria-describedby={recusa?.motivo === "nome" ? `${id}-nome-erro` : undefined}
            data-campo="nome"
          />
          {erroDe("nome")}
        </div>
        <div>
          <label htmlFor={`${id}-email`} className="text-sm font-semibold text-tinta">
            E-mail
          </label>
          <input
            id={`${id}-email`}
            type="email"
            className={`${CAMPO} mt-1`}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={recusa?.motivo === "email"}
            aria-describedby={recusa?.motivo === "email" ? `${id}-email-erro` : undefined}
            data-campo="email"
          />
          {erroDe("email")}
        </div>
        {comTelefone && (
          <div>
            <label htmlFor={`${id}-telefone`} className="text-sm font-semibold text-tinta">
              Telefone / WhatsApp com DDD
            </label>
            <input
              id={`${id}-telefone`}
              type="tel"
              inputMode="tel"
              className={`${CAMPO} mt-1`}
              autoComplete="tel"
              placeholder="(11) 99999-9999"
              value={telefone}
              onChange={(e) => setTelefone(mascararTelefone(e.target.value))}
              aria-invalid={recusa?.motivo === "telefone"}
              aria-describedby={recusa?.motivo === "telefone" ? `${id}-telefone-erro` : undefined}
              data-campo="telefone"
            />
            {erroDe("telefone")}
          </div>
        )}
        <p className="text-xs leading-relaxed text-cinza">
          Seus dados são usados para emitir o certificado e para a comunicação do curso, conforme a LGPD. Não são
          compartilhados com terceiros.
        </p>
        {recusa && !campos.includes(recusa.motivo) && <Recado tipo="erro" texto={recusa.mensagem} />}
        <button type="submit" className="pill-cta w-full" disabled={enviando} data-enviar>
          {enviando ? "Enviando…" : botao}
        </button>
      </form>
    </Moldura>
  );
}

function useCurso(slug: string | undefined, aula?: number) {
  const [r, setR] = useState<{ ok: true; curso: CursoPublico; aula: AulaPublica } | Recusa | "carregando">("carregando");
  useEffect(() => {
    let vivo = true;
    setR("carregando");
    void lerCurso(slug ?? "", aula).then((x) => {
      if (vivo) setR(x);
    });
    return () => {
      vivo = false;
    };
  }, [slug, aula]);
  return r;
}

// ---------------------------------------------------------------------------
// /certificados/p/<curso>/<aula>
// ---------------------------------------------------------------------------
export function PresencaGratuita() {
  const { slug, aula: aulaTexto } = useParams();
  const aula = Number(aulaTexto);
  const r = useCurso(slug, Number.isInteger(aula) ? aula : -1);
  const [feita, setFeita] = useState<{ ultima: boolean } | null>(null);

  if (r === "carregando") return <Carregando texto="Abrindo a lista de presença…" />;
  if (!r.ok) {
    const titulo = r.motivo === "aula" ? "Aula inválida" : r.motivo === "curso" ? "Curso não encontrado" : "Não conseguimos abrir a lista";
    return <Aviso titulo={titulo} texto={r.mensagem} />;
  }
  const { curso, aula: agenda } = r;

  if (feita) {
    return (
      <Moldura>
        <div className="surface p-7 text-center" data-presenca-confirmada>
          <h1 className="font-display text-2xl font-bold text-titulo">Presença confirmada!</h1>
          <p className="mt-2 text-sm text-cinza">
            Aula {aula} de {curso.num_aulas} — {curso.nome}.
          </p>
          {feita.ultima ? (
            <p className="mx-auto mt-5 max-w-md rounded-xl bg-chip px-4 py-3 text-sm leading-relaxed text-tinta" data-ultima-aula>
              O link para emitir o seu certificado será enviado por <strong>e-mail</strong> depois do encerramento do curso.
            </p>
          ) : (
            <p className="mt-5 text-sm text-cinza">Nos vemos na próxima aula!</p>
          )}
        </div>
      </Moldura>
    );
  }

  return (
    <FormularioDeDados
      titulo={`Presença — Aula ${aula} de ${curso.num_aulas}`}
      descricao={
        <>
          Confirme sua presença na <strong>Aula {aula}</strong> do curso <strong>{curso.nome}</strong>
          {agenda && (
            <>
              {" "}
              — dia <strong>{diaEMes(agenda.data)}</strong> às <strong>{agenda.hora}</strong>
            </>
          )}
          .{agenda?.titulo && <span className="mt-1 block">{agenda.titulo}</span>}
        </>
      }
      botao="Confirmar presença"
      comTelefone
      aoEnviar={async ({ nome, email, telefone }) => {
        const x = await assinarPresenca(curso.slug, aula, nome, email, telefone);
        if (!x.ok) return x;
        lembrarQuemAssinou.guardar(curso.slug, { nome: nome.trim(), email: email.trim() });
        setFeita({ ultima: x.ultima });
        return null;
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// /certificados/emitir/<curso>
// ---------------------------------------------------------------------------
export function EmitirGratuito() {
  const { slug } = useParams();
  const navegar = useNavigate();
  const r = useCurso(slug);

  if (r === "carregando") return <Carregando texto="Abrindo o curso…" />;
  if (!r.ok) {
    return <Aviso titulo={r.motivo === "curso" ? "Curso não encontrado" : "Não conseguimos abrir o curso"} texto={r.mensagem} />;
  }
  const { curso } = r;

  return (
    <FormularioDeDados
      titulo="Emitir certificado"
      descricao={
        <>
          Preencha seus dados para gerar o certificado de <strong>{curso.nome}</strong> ({curso.carga_horaria} horas,
          concluído em {dataDoDia(curso.data)}).
          {curso.requer_presenca && (
            <span className="mt-1 block" data-dica-presenca>
              Use o mesmo e-mail com que você assinou a lista de presença.
            </span>
          )}
        </>
      }
      botao="Gerar certificado"
      comTelefone={false}
      inicial={lembrarQuemAssinou.ler(curso.slug)}
      aoEnviar={async ({ nome, email }) => {
        const x = await emitirCertificado(curso.slug, nome, email);
        if (!x.ok) return x;
        lembrarQuemAssinou.guardar(curso.slug, { nome: nome.trim(), email: email.trim() });
        navegar(`/certificados/${x.token}`, { state: { jaEmitido: x.ja_emitido } });
        return null;
      }}
    />
  );
}

function useCertificado(token: string | undefined) {
  const [r, setR] = useState<CertificadoPublico | Recusa | "carregando">("carregando");
  useEffect(() => {
    let vivo = true;
    void lerCertificado(token ?? "").then((x) => {
      if (vivo) setR(x);
    });
    return () => {
      vivo = false;
    };
  }, [token]);
  return r;
}

// ---------------------------------------------------------------------------
// /certificados/<token>
// ---------------------------------------------------------------------------
export function CertificadoGratuitoPagina() {
  const { token } = useParams();
  const estado = useLocation().state as { jaEmitido?: boolean } | null;
  const r = useCertificado(token);

  if (r === "carregando") return <Carregando texto="Abrindo o seu certificado…" />;
  if (!r.ok) {
    return (
      <Aviso
        titulo={r.motivo === "nao_encontrado" ? "Certificado não encontrado" : "Não conseguimos abrir o certificado"}
        texto={r.motivo === "nao_encontrado" ? "Este link de certificado não é válido." : r.mensagem}
      />
    );
  }
  if (r.revogado) {
    return (
      <Aviso
        titulo="Certificado indisponível"
        texto="Este certificado foi revogado. Fale com a equipe da Contabilidade Facilitada."
        tom="erro"
      />
    );
  }

  async function imprimir() {
    // A fonte do papel tem de estar carregada antes: sem ela a folha sai na fonte de reserva.
    await document.fonts?.ready;
    window.print();
  }

  return (
    <Moldura largo>
      <div className="no-imprimir flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-titulo">Seu certificado</h1>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-cinza">
            Para guardar em PDF, clique em imprimir e escolha "Salvar como PDF" na janela que abrir.
          </p>
          {estado?.jaEmitido && (
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-cinza" data-ja-emitido>
              Este e-mail já tinha um certificado deste curso, e é ele que está aqui, com o nome da primeira emissão.
            </p>
          )}
        </div>
        <button type="button" onClick={() => void imprimir()} className="pill-cta" data-imprimir>
          Imprimir ou salvar em PDF
        </button>
      </div>
      <div className="mt-6 flex justify-center">
        <CertificadoGratuito
          nome={r.nome}
          curso={r.curso}
          horas={r.carga_horaria}
          data={r.data}
          validacao={enderecoDaValidacao(r.token)}
        />
      </div>
      <p className="no-imprimir mt-4 text-center text-sm text-cinza">
        <Link to={`/certificados/verificar/${r.token}`} className="font-semibold text-link underline">
          Ver a validação deste certificado
        </Link>
      </p>
    </Moldura>
  );
}

// ---------------------------------------------------------------------------
// /certificados/verificar/<token>
// ---------------------------------------------------------------------------
export function VerificarGratuito() {
  const { token } = useParams();
  const r = useCertificado(token);

  if (r === "carregando") return <Carregando texto="Verificando…" />;
  if (!r.ok) {
    return (
      <Aviso
        titulo={r.motivo === "nao_encontrado" ? "Certificado não encontrado" : "Erro na verificação"}
        texto={r.mensagem}
        tom="erro"
      />
    );
  }
  const emitido = new Date(r.emitido_em).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const linhas: [string, string][] = [
    ["Curso", r.curso],
    ["Carga horária", `${r.carga_horaria} horas`],
    ["Data do curso", dataDoDia(r.data)],
    ["Emitido em", emitido],
  ];

  return (
    <Moldura>
      <div className="surface p-7 text-center" data-validacao={r.revogado ? "revogado" : "valido"}>
        <p
          className={`text-xs font-bold tracking-widest uppercase ${r.revogado ? "text-erro-tinta" : "text-aviso-tinta"}`}
          data-situacao
        >
          {r.revogado ? "Certificado revogado" : "Certificado válido"}
        </p>
        <h1 className="mt-2 font-display text-2xl font-bold text-titulo" data-validacao-nome>
          {r.nome}
        </h1>
        <dl className="mt-6 space-y-3 text-left text-sm">
          {linhas.map(([rotulo, valor]) => (
            <div key={rotulo} className="flex justify-between gap-4 border-b border-borda pb-2">
              <dt className="text-cinza">{rotulo}</dt>
              <dd className="text-right font-semibold text-tinta" data-validacao-campo={rotulo}>
                {valor}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-8 text-xs text-cinza">Verificação oficial — Contabilidade Facilitada Educacional Ltda.</p>
      </div>
    </Moldura>
  );
}
